import icons from "../assets/icons";
import { IBlock, IEditorOptions, Property } from "../types";
import { copyEl, deleteEl } from "./eventHandlers";

export const editorOptions: IEditorOptions = {
  selectOnDrop: false,
  devices: [
    {
      name: "Desktop",
      icon: icons.desktop,
      width: "100%",
      default: true,
    },
    {
      name: "Tablet",
      icon: icons.tablet,
      width: "768px",
    },
    {
      name: "Phone",
      icon: icons.phone,
      width: "375px",
    },
  ],
  toolbars: [
    {
      name: "Copy",
      icon: icons.copy,
      click: copyEl,
    },
    {
      name: "Delete",
      icon: icons.delete,
      click: deleteEl,
    },
  ],
};

export const blocks: IBlock[] = [
  {
    type: "title",
    icon: '<svg viewBox="0 0 256 256" xmlns="http://www.w3.org/2000/svg"><path d="M219.994 55.995v32a12 12 0 1 1-24 0v-20h-56v120h20a12 12 0 0 1 0 24h-64a12 12 0 0 1 0-24h20v-120h-56v20a12 12 0 0 1-24 0v-32a12 12 0 0 1 12-12h160a12 12 0 0 1 12 12"/></svg>',
    name: "Title",
    content: "<h1>This is a Title</h1>",
    droppable: false,
    draggable: true,
  },
  {
    type: "block",
    icon: '<svg viewBox="0 0 36 36" xmlns="http://www.w3.org/2000/svg"><path d="M32 30H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h28a2 2 0 0 1 2 2v20a2 2 0 0 1-2 2M4 8v20h28V8Z" class="clr-i-outline clr-i-outline-path-1"/><path d="M9 25.3a.8.8 0 0 1-.8-.8v-13a.8.8 0 0 1 1.6 0v13a.8.8 0 0 1-.8.8" class="clr-i-outline clr-i-outline-path-2"/><path d="M14.92 25.3a.8.8 0 0 1-.8-.8v-13a.8.8 0 0 1 1.6 0v13a.8.8 0 0 1-.8.8" class="clr-i-outline clr-i-outline-path-3"/><path d="M21 25.3a.8.8 0 0 1-.8-.8v-13a.8.8 0 0 1 1.6 0v13a.8.8 0 0 1-.8.8" class="clr-i-outline clr-i-outline-path-4"/><path d="M27 25.3a.8.8 0 0 1-.8-.8v-13a.8.8 0 0 1 1.6 0v13a.8.8 0 0 1-.8.8" class="clr-i-outline clr-i-outline-path-5"/><path fill="none" d="M0 0h36v36H0z"/></svg>',
    name: "Block",
    content: "<div>This is a div </div>",
    droppable: true,
    draggable: true,
  },
];

export const properties: Record<string, Property[]> = {
  General: [
    {
      name: "Height",
      property: "height",
      type: "input",
    },
    {
      name: "Width",
      property: "width",
      type: "input",
    },
    {
      name: "Box Sizing",
      property: "box-sizing",
      type: "select",
      options: ["border-box", "content-box"],
      defaultValue: "",
    },
  ],
  Spacing: [
    {
      name: "Margin",
      property: "margin",
      type: "multi-input",
      options: ["top", "right", "bottom", "left"],
    },
    {
      name: "Padding",
      property: "padding",
      type: "multi-input",
      options: ["top", "right", "bottom", "left"],
    },
  ],
  Layout: [
    {
      name: "Display",
      property: "display",
      type: "select",
      options: [
        "block",
        "inline",
        "inline-block",
        "flex",
        "grid",
        "table",
        "none",
      ],
    },
    {
      name: "Position",
      property: "position",
      type: "select",
      options: ["static", "relative", "fixed", "absolute", "sticky"],
    },
    {
      name: "Float",
      property: "float",
      type: "select",
      options: ["left", "right", "none"],
      defaultValue: "none",
    },
  ],
  Typography: [
    {
      name: "Font Size",
      property: "font-size",
      type: "input",
    },
    {
      name: "Font Weight",
      property: "font-weight",
      type: "select",
      options: ["100", "200", "300", "400", "500", "600", "700", "800", "900"],
      defaultValue: "400",
    },
    {
      name: "Color",
      property: "color",
      type: "color",
    },
    {
      name: "Line Height",
      property: "line-height",
      type: "input",
    },
    {
      name: "Text Align",
      property: "text-align",
      type: "select",
      options: ["left", "right", "center"],
    },
    {
      name: "Text Decoration",
      property: "text-decoration",
      type: "select",
      options: ["none", "underline", "overline", "line-through"],
      defaultValue: "none",
    },
    {
      name: "Text Transform",
      property: "text-transform",
      type: "select",
      options: ["none", "capitalize", "uppercase", "lowercase"],
      defaultValue: "none",
    },
  ],
  Background: [
    {
      name: "Background Color",
      property: "background-color",
      type: "color",
    },
    // TODO: Add background image support
    {
      name: "Opacity",
      property: "opacity",
      type: "input",
    },
  ],
  Border: [
    {
      name: "Border Style",
      property: "border-style",
      type: "select",
      options: ["left", "right", "none"],
      defaultValue: "none",
    },
    {
      name: "Border Radius",
      property: "border-radius",
      type: "input",
    },
  ],
  Flexbox: [
    {
      name: "Flex Direction",
      property: "flex-direction",
      type: "select",
      options: ["row", "row-reverse", "column", "column-reverse"],
      defaultValue: "",
    },
    {
      name: "Justify Content",
      property: "justify-content",
      type: "select",
      options: [
        "flex-start",
        "flex-end",
        "center",
        "space-between",
        "space-around",
        "space-evenly",
      ],
      defaultValue: "flex-start",
    },
    {
      name: "Align Items",
      property: "align-items",
      type: "select",
      options: [
        "normal",
        "stretch",
        "center",
        "flex-start",
        "flex-end",
        "start",
        "end",
        "baseline",
      ],
      defaultValue: "normal",
    },
    {
      name: "Gap",
      property: "gap",
      type: "input",
    },
  ],
  Posittioning: [
    {
      name: "Top",
      property: "top",
      type: "input",
    },
    {
      name: "Right",
      property: "right",
      type: "input",
    },
    {
      name: "Bottom",
      property: "bottom",
      type: "input",
    },
    {
      name: "Left",
      property: "left",
      type: "input",
    },
    {
      name: "Z Index",
      property: "z-index",
      type: "input",
    },
  ],
  Others: [
    {
      name: "Cursor",
      property: "cursor",
      type: "select",
      options: [
        "alias",
        "all-scroll",
        "auto",
        "cell",
        "col-resize",
        "context-menu",
        "copy",
        "crosshair",
        "default",
        "e-resize",
        "ew-resize",
        "grab",
        "grabbing",
        "help",
        "move",
        "n-resize",
        "ne-resize",
        "nesw-resize",
        "ns-resize",
        "nw-resize",
        "nwse-resize",
        "no-drop",
        "none",
        "not-allowed",
        "pointer",
        "progress",
        "row-resize",
        "s-resize",
        "se-resize",
        "sw-resize",
        "text",
        "URL",
        "vertical-text",
        "w-resize",
        "wait",
        "zoom-in",
        "zoom-out",
      ],
      defaultValue: "default",
    },
    {
      name: "Overflow",
      property: "overflow",
      type: "select",
      options: ["visible", "hidden", "clip", "scroll", "auto"],
      defaultValue: "visible",
    },
    {
      name: "Visibility",
      property: "visibility",
      type: "select",
      options: ["visible", "hidden", "collapse"],
      defaultValue: "visible",
    },
    {
      name: "User Select",
      property: "user-select",
      type: "select",
      options: ["auto", "none", "text", "all"],
      defaultValue: "auto",
    },
  ],
};
